/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import _get from 'lodash/get'
import PropTypes from 'prop-types'
import React from 'react'
import { compose, withHandlers, lifecycle, setPropTypes } from 'recompose'
import { connect } from 'react-redux'
import { change } from 'redux-form'
import { NO_AVAILABLE_COUNTRY } from 'constants/countries'
import { Text } from 'hocs/localization'
import { openModal, closeModal } from 'redux-app/modules/modals'
import { getCountryByCountryIdState } from 'api/client/actions/country'
import {
  getLocalizedCompanyCountryResidenceOptions,
  getLocalizedStateOptions,
  getLocalizedLimitedStateOptions,
  getLocalizedBillingCountryOptions,
} from 'redux-app/selectors/apiData'
import Modal, { ModalMessage, ModalFooter, ModalFooterButtons } from 'apps/Modal'
import Button from 'elements/Button'

const MODAL_OTHER_COUNTRIES = 'notAvailableCountries'
const COUNTRY_KEY = 'countryId'
const STATE_KEY = 'stateId'

export enum FORM_COUNTRY_TYPES {
  all,
  billing,
}

interface IProps {
  t: Text,
  openModal: (params: { name: string }) => void,
  closeModal: (params: { name: string }) => void,
}

const localizationPropTypes = {
  t: PropTypes.func.isRequired,
  texts: PropTypes.object.isRequired,
}

const getCountryOptionsSelectorByCountryType = countryType => {
  if (countryType === FORM_COUNTRY_TYPES.all) {
    return getLocalizedCompanyCountryResidenceOptions
  }
  return getLocalizedBillingCountryOptions
}

const WithNotAvailableCountryModalHoc = WrappedComponent =>
  class WithNotAvailableCountryModal extends React.Component<IProps> {
    private onOpenNotAvailableCountriesModal = () => {
      this.props.openModal({ name: MODAL_OTHER_COUNTRIES })
    }

    private onCloseOtherCountriesModal = () => {
      this.props.closeModal({ name: MODAL_OTHER_COUNTRIES })
    }

    public render () {
      const { t } = this.props

      return (
        <React.Fragment>
          <WrappedComponent {...this.props} onOpenNotAvailableCountriesModal={this.onOpenNotAvailableCountriesModal} />
          <Modal name={MODAL_OTHER_COUNTRIES}>
            <ModalMessage>
              {t('notAvailableCountriesModal.message')}
            </ModalMessage>
            <ModalFooter>
              <ModalFooterButtons>
                <Button onClick={this.onCloseOtherCountriesModal} size='small'>
                  {t('notAvailableCountriesModal.okButton')}
                </Button>
              </ModalFooterButtons>
            </ModalFooter>
          </Modal>
        </React.Fragment>
      )
    }
  }

export const withNotAvailableCountryModal = WrappedComponent => compose(
  connect(
    null,
    {
      closeModal,
      openModal,
    },
  ),
  WithNotAvailableCountryModalHoc,
)(WrappedComponent)

const withOnChangeCountryHandler = WrappedComponent => compose(
  withHandlers({
    onChangeCountry: ({ openModal, onChangeFieldValue, onStatesRequest }) => (event, newValue) => {
      if (newValue === NO_AVAILABLE_COUNTRY) {
        event.preventDefault()
        openModal({ name: MODAL_OTHER_COUNTRIES })
        return
      }

      onChangeFieldValue({ name: STATE_KEY, value: null })
      onStatesRequest({ countryId: newValue })
    },
  }),
)(WrappedComponent)

const withCountriesLoading = countryType => WrappedComponent => compose(
  setPropTypes({ ...localizationPropTypes }),
  connect(
    (state, props) => ({
      countryOptions: getCountryOptionsSelectorByCountryType(countryType)(state, props),
    }),
    {
      // getCountry: countryType === FORM_COUNTRY_TYPES.all ? getCountry : getCountryBilling,
    },
  ),
  lifecycle({
    componentDidMount () {
      // this.props.getCountry()
    },
  }),
)(WrappedComponent)

export const withStatesLoading = (countryKey = COUNTRY_KEY) => WrappedComponent => compose(
  setPropTypes({ ...localizationPropTypes }),
  connect(
    (state, props) => ({
      stateOptions: getLocalizedStateOptions(state, props),
      stateLimitedOptions: getLocalizedLimitedStateOptions(state, props),
    }),
    {
      onStatesRequest: getCountryByCountryIdState,
    },
  ),
  lifecycle({
    componentDidMount () {
      const { onStatesRequest, initialValues } = this.props
      const countryId = _get(initialValues, countryKey) || 1

      countryId && onStatesRequest({ countryId })
    },

    componentWillReceiveProps (nextProps) {
      if (_get(this.props, ['initialValues', countryKey]) !== _get(nextProps, ['initialValues', countryKey])) {
        const countryId = _get(nextProps.initialValues, countryKey)
        countryId && this.props.onStatesRequest({ countryId })
      }
    },
  }),
)(WrappedComponent)

export const formWithAddress = (countryType = FORM_COUNTRY_TYPES.all) => WrappedComponent => compose(
  setPropTypes({ ...localizationPropTypes }),
  connect(
    (state, props) => ({
      countryOptions: getCountryOptionsSelectorByCountryType(countryType)(state, props),
    }),
    {
      openModal,
      change,
    },
  ),
  withHandlers({
    onChangeFieldValue: ({ change }) => ({ name, value }) => change(name, value),
  }),
  withCountriesLoading(countryType),
  withStatesLoading(COUNTRY_KEY),
  withNotAvailableCountryModal,
  withOnChangeCountryHandler,
)(WrappedComponent)
